import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import TextHeader from "./TextHeader";
import useStarfishAPI from "../hooks/useStarfishAPI";
import {urlParams} from "../utils/request";
import useError from "../hooks/useError";
import GroupSession from "./GroupSession";
import css from "./GroupSelection.css"
import {Card} from "semantic-ui-react";
import ContinueButton from "./ContinueButton";
import SubmitStudentAPI from "./SubmitStudentAPI";

function GroupSelection(props) {
  const [selectedGroupSession, setSelectedGroupSession] = useState(null);

  const [{data: groupSessionsData, success: groupSessionSuccess, error: groupSessionError, pending: groupSessionPending}, callAPI] = useStarfishAPI();
  const [{data: relatedCoursesData, error: relatedCoursesError, pending: relatedCoursePending}, relatedCoursesCallAPI] = useStarfishAPI();
  const [{success: submitStudentSuccess, error: submitStudentError}, {onSubmitStudentApi}] = SubmitStudentAPI();

  const getGroups = () => {
    const params = urlParams({studentId: props.studentId, serviceId: props.serviceId}, false);
    const URL = `../kiosk/getGroupSessionForKiosk.html?${params}`;
    callAPI(URL, {
      method: 'GET',
    });
  };

  const getStudentRelatedCourses = () => {
    const url = `../kiosk/getCoursesForStudent.html?${urlParams({studentId: props.studentId})}`;
    return relatedCoursesCallAPI(url);
  };

  const filterGroupSessions = (coursesForStudent, groupSessions) => {
    return groupSessions.filter((groupSession) => {
      const { groupSessionSupplementalInstructionRestriction, courseRestrictions, appointmentTypeReason } = groupSession;
      if (props.selectedReasonId !== appointmentTypeReason.id) return false;
      if ( !groupSession.supplementalInstruction || !courseRestrictions || courseRestrictions.length < 1 ) return true;
      if (groupSessionSupplementalInstructionRestriction === 'SECTION') {
        return courseRestrictions.some((courseRestriction) =>
          coursesForStudent.map(({sectionId}) => sectionId).indexOf(courseRestriction.sectionId) > -1);
      }
      if (groupSessionSupplementalInstructionRestriction === 'COURSE') {
        return courseRestrictions.some((courseRestriction) =>
          coursesForStudent.map(({courseId}) => courseId).indexOf(courseRestriction.courseId) > -1);
      }
      return true;
    });
  };

  if (submitStudentSuccess) {
    props.goToConfirmationPage();
  }

  const shouldNotSelectCourse = () => !selectedGroupSession.supplementalInstruction || selectedGroupSession.courseRestrictions.length;

  const selectGroupSession = () => {
    if (props.shouldSkipCourseAndMoreInformationPages && shouldNotSelectCourse()){
      const params = urlParams({
        studentId: props.studentId,
        serviceId: props.serviceId,
        reasonId: props.selectedReasonId,
        kioskId: props.kioskId,
        courseId: 0,
        groupSessionId: selectedGroupSession.groupSessionId,
      }, false);
      onSubmitStudentApi(params);
    } else {
      props.onSelectGroupSession(selectedGroupSession);
    }
  };

  useError(groupSessionError, props.setError);
  useError(relatedCoursesError, props.setError);
  useError(submitStudentError, props.setError);

  useEffect(() => {
    getGroups();
    getStudentRelatedCourses();
  }, [props.studentId, props.serviceId]);

  if (groupSessionPending || relatedCoursePending) return <div>Loading groups...</div>;
  if (!groupSessionSuccess || !groupSessionsData.result || !groupSessionsData.result.length) {
    return <TextHeader lowerText={'No groups found for you'}/>;
  } else {
    const filteredGroupSessions = filterGroupSessions(relatedCoursesData.result, groupSessionsData.result);
    return (
      <div>
        <TextHeader
          id="groupSessionSelection"
          upperText={props.groupSessionGreetingText}
          lowerText={props.groupSessionMainText}
        />
        <div className={css['container']}>
          <Card.Group className={css['card-group']} aria-labelledby="groupSessionSelection">
            {filteredGroupSessions.map((groupSession) =>
              <GroupSession
                groupSession={groupSession}
                selectGroupSession={setSelectedGroupSession}
                isChecked={!!selectedGroupSession && groupSession.id === selectedGroupSession.id}
                key={groupSession.id}
              />
            )}
          </Card.Group>
          <ContinueButton onClick={selectGroupSession}
                          disabled={!selectedGroupSession}/>
        </div>
      </div>
    );
  }
}

GroupSelection.propTypes = {
  onSelectGroupSession: PropTypes.func.isRequired,
  studentId: PropTypes.number.isRequired,
  serviceId: PropTypes.number.isRequired,
  setError: PropTypes.func.isRequired,
  groupSessionGreetingText: PropTypes.string,
  groupSessionMainText: PropTypes.string,
  selectedReasonId: PropTypes.number.isRequired,
  shouldSkipCourseAndMoreInformationPages: PropTypes.bool.isRequired,
  kioskId: PropTypes.number.isRequired,
  goToConfirmationPage: PropTypes.func.isRequired
};

export default GroupSelection;
